import { useState, useRef, useEffect } from "react";
import { useTutorialState } from "../../hooks/useTutorialState";
import { useMotionValue, useTransform, PanInfo } from "framer-motion";
import type { Recipe } from "../../types/recipe";
import type { V32UITier } from '../../types/subscription';
import RecipeSwiperView from './RecipeSwiper.view';

interface RecipeSwiperContainerProps {
  recipes: Recipe[];
  onSwipeRight: (recipe: Recipe) => void;
  onSwipeLeft: (recipe: Recipe) => void;
  onSwipeUp: (recipe: Recipe) => void;
  onSwipeDown: (recipe: Recipe) => void;
  onComplete: (selectedRecipes: Recipe[], savedRecipes: Recipe[]) => void;
  onBack: () => void;
  onRefresh?: () => void;
  refreshing?: boolean;
  showProgress?: boolean;
  enableHaptics?: boolean;
  currentTier?: V32UITier;
  onUpgrade?: () => void;
  // Phase 3: Pre-seeding support
  preSeededRecipes?: Recipe[];
  shouldPreSeed?: boolean;
  // Add missing fields for Recipe type
  createdAt?: Date;
  updatedAt?: Date;
  servings?: number;
  slug?: string;
}

/**
 * RecipeSwiper Container - Centralizes all business logic for recipe swiping interface
 * 
 * This centralizes:
 * - Advanced motion/animation control with Framer Motion
 * - Complex multi-directional swipe gesture recognition and velocity analysis
 * - Tutorial state management and onboarding flow coordination
 * - Haptic feedback system with device vibration patterns
 * - Progress tracking and recipe navigation state management
 * - Stack visualization logic for multiple recipe previews
 * - Swipe direction analysis algorithms with velocity thresholds
 * - Animation timing and overlay coordination
 * - Recipe selection and saving state management
 * - Tier-based upgrade logic and feature gating
 * - All action handlers and complex gesture business logic
 */
export default function RecipeSwiperContainer({
  recipes,
  preSeededRecipes,
  shouldPreSeed = true,
  onSwipeRight,
  onSwipeLeft,
  onSwipeUp,
  onSwipeDown,
  onComplete,
  onBack,
  onRefresh,
  refreshing = false,
  showProgress = true,
  enableHaptics = true,
  currentTier = "demo",
  onUpgrade,
}: RecipeSwiperContainerProps) {
  // Tutorial state management
  const { isFirstVisit, completeTutorial, resetTutorial } = useTutorialState("recipeDeckTutorial");

  // Business logic state - Recipe navigation and selection
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedRecipes, setSelectedRecipes] = useState<Recipe[]>([]);
  const [savedRecipes, setSavedRecipes] = useState<Recipe[]>([]);
  const [swipeDirection, setSwipeDirection] = useState<string | null>(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const [tutorialCompleted, setTutorialCompleted] = useState(false);

  // Phase 3: Merge pre-seeded recipes with deck
  const [mergedRecipes, setMergedRecipes] = useState<Recipe[]>(recipes);

  useEffect(() => {
    if (shouldPreSeed && preSeededRecipes && preSeededRecipes.length > 0) {
      // Pre-seeded recipes come first, then additional recipes
      setMergedRecipes([...preSeededRecipes, ...recipes]);
    } else {
      setMergedRecipes(recipes);
    }
  }, [shouldPreSeed, preSeededRecipes, recipes]);

  // Business logic: Framer Motion values for gesture recognition
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotate = useTransform(x, [-300, 0, 300], [-30, 0, 30]);
  const opacity = useTransform(
    x,
    [-300, -150, 0, 150, 300],
    [0, 0.5, 1, 0.5, 0],
  );

  // Business logic: Computed values
  const currentRecipe = mergedRecipes[currentIndex];
  const progress = ((currentIndex + 1) / mergedRecipes.length) * 100;

  // Business logic: Haptic feedback system
  const triggerHaptic = (type: "light" | "medium" | "heavy" = "light") => {
    if (enableHaptics && "vibrate" in navigator) {
      const patterns = {
        light: [10],
        medium: [50],
        heavy: [100],
      };
      navigator.vibrate(patterns[type]);
    }
  };

  // Business logic: Advanced gesture recognition and direction analysis
  const handleDragEnd = (event: any, info: PanInfo) => {
    const offset = info.offset.x;
    const velocity = info.velocity.x;
    const yOffset = info.offset.y;

    // Determine swipe direction based on offset and velocity
    if (Math.abs(yOffset) > Math.abs(offset) && Math.abs(yOffset) > 100) {
      // Vertical swipe
      if (yOffset < 0 && (yOffset < -100 || info.velocity.y < -500)) {
        // Up swipe - Save for later
        handleSwipe("up");
        return;
      } else if (yOffset > 0 && (yOffset > 100 || info.velocity.y > 500)) {
        // Down swipe - Maybe later
        handleSwipe("down");
        return;
      }
    } else {
      // Horizontal swipe
      if (offset > 100 || velocity > 500) {
        // Right swipe - Add to plan
        handleSwipe("right");
        return;
      } else if (offset < -100 || velocity < -500) {
        // Left swipe - Skip
        handleSwipe("left");
        return;
      }
    }

    // Reset position if not enough momentum
    x.set(0);
    y.set(0);
  };

  // Business logic: Swipe action coordination and state management
  const handleSwipe = (direction: "left" | "right" | "up" | "down") => {
    if (!currentRecipe) return;

    setSwipeDirection(direction);
    setShowOverlay(true);
    triggerHaptic("medium");

    // Execute the appropriate action
    switch (direction) {
      case "right":
        setSelectedRecipes((prev) => [...prev, currentRecipe]);
        onSwipeRight(currentRecipe);
        break;
      case "left":
        onSwipeLeft(currentRecipe);
        break;
      case "up":
        setSavedRecipes((prev) => [...prev, currentRecipe]);
        onSwipeUp(currentRecipe);
        break;
      case "down":
        onSwipeDown(currentRecipe);
        break;
    }

    // Advance to next recipe with animation timing
    setTimeout(() => {
      if (currentIndex < mergedRecipes.length - 1) {
        setCurrentIndex(currentIndex + 1);
        setShowOverlay(false);
        setSwipeDirection(null);
        x.set(0);
        y.set(0);
      } else {
        onComplete(selectedRecipes, savedRecipes);
      }
    }, 300);
  };

  // Business logic: Button action handler
  const buttonAction = (direction: "left" | "right" | "up" | "down") => {
    handleSwipe(direction);
  };

  // Business logic: Swipe overlay styling logic
  const getOverlayStyle = (direction: string) => {
    switch (direction) {
      case "right":
        return {
          background:
            "linear-gradient(135deg, #FFBB55 0%, rgba(255, 187, 85, 0.9) 100%)",
          icon: "✅",
          text: "I'll cook this!",
          color: "#1F2937",
        };
      case "left":
        return {
          background:
            "linear-gradient(135deg, #6B7280 0%, rgba(107, 114, 128, 0.9) 100%)",
          icon: "❌",
          text: "Not for me",
          color: "#FFFFFF",
        };
      case "up":
        return {
          background:
            "linear-gradient(180deg, #F3726A 0%, rgba(243, 114, 106, 0.9) 100%)",
          icon: "⭐",
          text: "Save for later",
          color: "#FFFFFF",
        };
      case "down":
        return {
          background:
            "linear-gradient(180deg, #A6D4F9 0%, rgba(166, 212, 249, 0.9) 100%)",
          icon: "🕐",
          text: "Maybe later",
          color: "#1F2937",
        };
      default:
        return null;
    }
  };

  // Business logic: Tutorial event handlers
  const handleTutorialComplete = (data: any) => {
    completeTutorial(data);
    setTutorialCompleted(true);
  };

  const handleTutorialSkip = () => {
    setTutorialCompleted(true);
  };

  const handleReplayTutorial = () => {
    resetTutorial();
    setTutorialCompleted(false);
  };

  // Business logic: Computed overlay style
  const overlayStyle = swipeDirection ? getOverlayStyle(swipeDirection) : null;

  // Business logic: Stack preview recipes calculation
  const stackPreviewRecipes = mergedRecipes.slice(currentIndex + 1, currentIndex + 3);

  // Map all props to the view
  const viewProps = {
    // Data
    recipes: mergedRecipes,
    currentRecipe,
    currentIndex,
    selectedRecipes,
    savedRecipes,
    swipeDirection,
    showOverlay,
    tutorialCompleted,
    progress,
    refreshing,
    showProgress,
    currentTier,
    isFirstVisit,
    stackPreviewRecipes,
    overlayStyle,
    
    // Framer Motion values
    x,
    y,
    rotate,
    opacity,
    
    // Event handlers - Navigation
    onBack,
    onRefresh,
    onUpgrade,
    
    // Event handlers - Swipe actions
    onDragEnd: handleDragEnd,
    onButtonAction: buttonAction,
    onComplete: () => onComplete(selectedRecipes, savedRecipes),
    
    // Event handlers - Tutorial
    onTutorialComplete: handleTutorialComplete,
    onTutorialSkip: handleTutorialSkip,
    onReplayTutorial: handleReplayTutorial,
  };

  return <RecipeSwiperView {...viewProps} />;
}

// Export types for the view component
export type { Recipe };